import { Mark, mergeAttributes } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    flowHighlight: {
      toggleHighlight: () => ReturnType;
    };
  }
}

/**
 * Text highlighting in LogicLine Orange.
 *
 * Serializes to `<mark>…</mark>` rather than the `==…==` convention: the editor
 * already runs `Markdown.configure({ html: true })`, so raw HTML round-trips
 * without a new remark plugin, and a Protokoll read outside Flow still shows the
 * emphasis. `MarkdownContent`'s rehype-sanitize schema must allow `mark` — GitHub's
 * `defaultSchema` does not — otherwise the tag is silently stripped on render while
 * still looking correct in the editor.
 *
 * No attributes are carried: the colour lives in CSS (`.flow-md mark`,
 * `.flow-rte-content mark`), so a stored body can never smuggle a `style`.
 * Written as a local Mark instead of pulling in `@tiptap/extension-highlight`,
 * which would need this same markdown glue anyway.
 */
export const HighlightMark = Mark.create({
  name: "highlight",

  parseHTML() {
    return [{ tag: "mark" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["mark", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      toggleHighlight:
        () =>
        ({ commands }) =>
          commands.toggleMark(this.name),
    };
  },

  addKeyboardShortcuts() {
    // Ctrl/Cmd+Shift+H — free in this editor (Ctrl+Enter finishes, the @-popup
    // owns Enter, the toolbar owns the rest).
    return {
      "Mod-Shift-h": () => this.editor.commands.toggleHighlight(),
    };
  },

  addStorage() {
    return {
      markdown: {
        // Mirrors tiptap-markdown's own mark specs (see its strike.js).
        // expelEnclosingWhitespace keeps `<mark>a </mark>b` from becoming
        // `<mark>a</mark> b`-with-a-stray-space on round-trip.
        serialize: { open: "<mark>", close: "</mark>", expelEnclosingWhitespace: true },
        // Parsing is free: html:true lets markdown-it emit the raw <mark>, which
        // this mark's parseHTML then claims.
        parse: {},
      },
    };
  },
});
